/**
 * Runs a check on an interval until the given condition is met (resolves the promise),
 * or until a given time has passed (timeout), in which case it rejects the promise.
 *
 * @param {function} condition
 * @param {number} [intervalMs] How often to run the check
 * @param {number} [timeoutMs] The ultimate timeout
 * @return {Promise<void>}
 */
export const waitUntil = (condition: () => any, intervalMs = 300, timeoutMs = 5000): Promise<void> => new Promise((resolve, reject) => {
	const cleanup = function () {
		if (interval)
			clearInterval(interval);
		if (timeout)
			clearTimeout(timeout);
	};

	const interval = setInterval(function () {
		if (condition()) {
			cleanup();
			resolve();
		}
	}, intervalMs);

	const timeout = setTimeout(function () {
		cleanup();
		reject("waitUntil() timed out");
	}, timeoutMs);
});

/**
 * This function creates a controlled promise, which allows to resolve/reject a promise outside of its own callback.
 * Also, It provides information about the promise status, like isFulfilled, isRejected, using simple functions.
 */
export const ControlledPromise = function () {
	this.resolve = null;
	this.reject = null;

	let _isPending = false;
	let _isFulfilled = false;
	let _isRejected = false;
	// @ts-expect-error TS(7034) FIXME: Variable '_value' implicitly has type 'any' in som... Remove this comment to see the full error message
	let _value;
	// @ts-expect-error TS(7034) FIXME: Variable '_promise' implicitly has type 'any' in s... Remove this comment to see the full error message
	let _promise;

	// @ts-expect-error TS(7006) FIXME: Parameter 'callback' implicitly has an 'any' type.
	this.start = (callback, ...args) => {
		// @ts-expect-error TS(7005) FIXME: Variable '_promise' implicitly has an 'any' type.
		if (!_promise) {
			_promise = new Promise((resolve, reject) => {
				_isPending = true;

				// @ts-expect-error TS(7006) FIXME: Parameter 'value' implicitly has an 'any' type.
				this.resolve = (value) => {
					if (!_isPending)
						return;

					_isPending = false;
					_isFulfilled = true;
					_value = value;
					resolve(value);
				};

				// @ts-expect-error TS(7006) FIXME: Parameter 'error' implicitly has an 'any' type.
				this.reject = (error) => {
					if (!_isPending)
						return;

					_isPending = false;
					_isRejected = true;
					reject(error);
				};
			});

			if (callback && typeof callback === "function") {
				try {
					const result = callback(...args);

					if (result && typeof result.then === "function") {
						// @ts-expect-error TS(7006) FIXME: Parameter 'value' implicitly has an 'any' type.
						result.then(value => {
							this.resolve(value);
							// @ts-expect-error TS(7006): Parameter 'e' implicitly has an 'any' type.
						}, e => {
							this.reject(e);
						});
					} else if (result)
						this.resolve(result);
				} catch (e) {
					this.reject(e);
				}
			}
		}

		return _promise;
	};

	// @ts-expect-error TS(7005) FIXME: Variable '_promise' implicitly has an 'any' type.
	this.getPromise = () => _promise;
	this.isFulfilled = () => _isFulfilled;
	this.isRejected = () => _isRejected;
	// @ts-expect-error TS(7005) FIXME: Variable '_value' implicitly has an 'any' type.
	this.getValue = () => _value;
	this.isPending = () => _isPending;
};

// @ts-expect-error TS(7006) FIXME: Parameter 'callback' implicitly has an 'any' type.
export const exponentialBackoff = (callback, options = {}) => {
	let attempt = 1;
	// @ts-expect-error TS(7034) FIXME: Variable 'timeout' implicitly has type 'any' in so... Remove this comment to see the full error message
	let timeout;
	// @ts-expect-error TS(7034) FIXME: Variable 'timer' implicitly has type 'any' in some... Remove this comment to see the full error message
	let timer;
	// @ts-expect-error TS(7034) FIXME: Variable 'currentPromise' implicitly has type 'any... Remove this comment to see the full error message
	let currentPromise;

	const {retries, minTimeout, maxTimeout, exponentialFactor} = {
		retries:           0,
		minTimeout:        500,
		maxTimeout:        Infinity,
		exponentialFactor: 2,
		...options
	};

	// @ts-expect-error TS(7019) FIXME: Rest parameter 'args' implicitly has an 'any[]' ty... Remove this comment to see the full error message
	const retryFunction = (...args) => {
		// @ts-expect-error TS(7005) FIXME: Variable 'currentPromise' implicitly has an 'any' ... Remove this comment to see the full error message
		if (!currentPromise) {
			timeout = _calcExpTimeout(attempt, minTimeout, maxTimeout, exponentialFactor);

			currentPromise = new Promise(resolve => {
				if (retries > 0 && attempt > retries) {
					retryFunction.cancel();
					throw new Error("Maximum retries achieved");
				}

				timer = setTimeout(() => {
					const result = callback(...args);

					// @ts-expect-error TS(7019) FIXME: Rest parameter 'values' implicitly has an 'any[]' ... Remove this comment to see the full error message
					const stop = (...values) => {
						attempt++;
						// @ts-expect-error TS(2556) FIXME: A spread argument must either have a tuple type or... Remove this comment to see the full error message
						resolve(...values);
						currentPromise = null;
					};

					if (result && typeof result.then === "function")
					// @ts-expect-error TS(7006) FIXME: Parameter 'error' implicitly has an 'any' type.
						result.then(stop).catch((error) => stop(undefined, error));
					else
						stop(result);
					// @ts-expect-error TS(7005): Variable 'timeout' implicitly has an 'any' type.
				}, timeout);
			});
		}

		return currentPromise;
	};

	retryFunction.cancel = () => {
		// @ts-expect-error TS(7005) FIXME: Variable 'timer' implicitly has an 'any' type.
		if (timer)
			clearTimeout(timer);

		attempt = 1;

		// note that the context that called us will still have a reference to the promise,
		// which was returned from retryFunction(), even though we're clearing our
		// reference to it. We do this so retryFunction() will work fine when called again.
		currentPromise = null;
	};

	// @ts-expect-error TS(7005) FIXME: Variable 'timeout' implicitly has an 'any' type.
	retryFunction.getCurrentTimeout = () => timeout;
	retryFunction.getCurrentAttempt = () => attempt - 1;

	return retryFunction;
};

/**
 * This function retries a given function until a given condition is met. It uses exponential backoff
 *
 * @param {function} callback
 * @param {function} resultFn
 * @param {object} options
 * @param {number} [options.retries]
 * @param {number} [options.minTimeout]
 * @param {number} [options.maxTimeout]
 * @param {number} [options.exponentialFactor]
 * @return {function}
 */
export const retryFunctionUntil = <T extends (...args: any[]) => Promise<any>>(
	callback: T,
	resultFn: (result: Awaited<ReturnType<T>>) => boolean,
	options: {
		retries?:           number;
		minTimeout?:        number;
		maxTimeout?:        number;
		exponentialFactor?: number;
	}
) => {
	const {retries, minTimeout, maxTimeout, exponentialFactor} = {
		retries:           0,
		minTimeout:        500,
		maxTimeout:        Infinity,
		exponentialFactor: 2,
		...options
	};

	let attempt = 1;
	let timer:NodeJS.Timeout;

	const promise = new Promise<Awaited<ReturnType<T>>>((resolve) => {
		const retryUntil = () => {
			const R = Math.random() + 1; // number in range [1-2]
			const timeout = Math.round(Math.min(R * minTimeout * Math.pow(exponentialFactor, attempt - 1), maxTimeout));

			if (timer) clearTimeout(timer);

			if (retries > 0 && attempt > retries)
				throw new Error("Maximum retries achieved");

			timer = setTimeout(() => {
				attempt++;
				const result = callback();

				result.then((value) => {
					if (resultFn(value)) {
						resolve(value);
						return;
					}

					retryUntil();
				}).catch((error) => {
					console.log("Error in retryFunctionUntil", error);
					retryUntil();
				});
			}, timeout);
		};

		retryUntil();
	});

	return promise;
};

/**
 * Unexported helper function to calculate timeouts.
 *
 * @param {number} attempt           >= 1
 * @param {number} minTimeout
 * @param {number} maxTimeout
 * @param {number} exponentialFactor
 * @return {number}
 */
// @ts-expect-error TS(7006) FIXME: Parameter 'attempt' implicitly has an 'any' type.
const _calcExpTimeout = (attempt, minTimeout, maxTimeout, exponentialFactor) => {
	// Timeout based on:
	// http://dthain.blogspot.com/2009/02/exponential-backoff-in-distributed.html

	const R = Math.random() + 1; // number in range [1-2]
	return Math.round(Math.min(R * minTimeout * Math.pow(exponentialFactor, attempt - 1), maxTimeout));
};

// Expose for testing purposes only
const __testFns = (process.env.NODE_ENV === "test")
	? {_calcExpTimeout}
	: undefined;

export default {
	waitUntil,
	ControlledPromise,
	exponentialBackoff,
	retryFunctionUntil,
	__testFns
};
